import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";
import { useProductUpload } from "../ProductSelector/useProductUpload";
import { resolvePlaygroundItemId } from "../../utils/playgroundDelete";

const DEFAULT_PAGE_LIMIT = 2; // Collections page limit matching playground

const DEFAULT_TOP_TITLE = "Top";
const DEFAULT_TRENDY_TITLE = "Trendy";

const normalizeProductItem = (item, collectionTitle = "") => {
  if (!item) return null;
  const activeFlag =
    item?.bIsActive !== undefined
      ? Boolean(item.bIsActive && item.bIsActive !== "0" && item.bIsActive !== 0)
      : item?.isActive !== undefined
        ? Boolean(item.isActive && item.isActive !== "0" && item.isActive !== 0)
        : item?.status !== undefined
          ? Boolean(item.status === true || item.status === 1 || item.status === "1" || item.status === "active")
          : true;

  const id = resolvePlaygroundItemId(item) || item.id || item.iProductId;

  return {
    ...item,
    id,
    iProductId: id,
    title: item.title || item.sTitle || item.name || "Untitled Product",
    sku: item.sku || item.vSku || item.vSKU || item.skuId || String(id || ""),
    price: item.price ?? item.decPrice ?? item.decPriceUsd ?? item.decPriceInr ?? 0,
    images: Array.isArray(item.images) && item.images.length > 0
      ? item.images
      : [item.image || item.front || item.sImage].filter(Boolean),
    collectionTitle: collectionTitle || item.collectionTitle || item.collectionName || "",
    category: item.category || item.categoryName || item.sCategory || "",
    bIsActive: activeFlag,
    isActive: activeFlag,
    generationCount: Number(item.generationCount) || 0,
  };
};

function normalizeProductCollections(rawData) {
  if (!rawData || typeof rawData !== "object") {
    return [];
  }

  let entries = [];

  if (rawData.collections && typeof rawData.collections === "object") {
    if (Array.isArray(rawData.collections)) {
      entries = rawData.collections.map((item, idx) => [
        item.title || item.collectionName || item.name || `Collection ${idx + 1}`,
        Array.isArray(item.products) ? item.products : Array.isArray(item.items) ? item.items : [],
      ]);
    } else {
      entries = Object.entries(rawData.collections).filter(([, value]) => Array.isArray(value));
    }
  } else {
    // Fallback to rawData root keys if collections is missing
    entries = Object.entries(rawData).filter(
      ([key, value]) =>
        Array.isArray(value) &&
        !["rows", "filters", "categories", "filterCategories"].includes(key)
    );
  }

  return entries.map(([title, list]) => {
    const normalizedItems = Array.isArray(list)
      ? list.map((item) => normalizeProductItem(item, title)).filter(Boolean)
      : [];
    return {
      id: title,
      title: title,
      products: normalizedItems,
    };
  });
}

export const useProductListing = () => {
  const { role: userRole, token } = useSelector((state) => state.auth || {});

  // Filters & Parameters
  const [category, setCategory] = useState("women"); // "men" or "women"
  const [modelType, setModelType] = useState("model"); // "model" or "mannequin"
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [apiFilters, setApiFilters] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Collections state
  const [rawCollections, setRawCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modals state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isBulkImageUploadModalOpen, setIsBulkImageUploadModalOpen] = useState(false);

  // Zoom Modal
  const [zoomModal, setZoomModal] = useState({ front: "", back: "" });

  const {
    uploadProduct,
    updateProduct,
    fetchFilterCategories,
    fetchCollectionCategories,
    isLoading: isProductUploadLoading,
  } = useProductUpload();

  const mappedModelType = modelType === "mannequin" ? "mannequin_to_model" : "model_to_model";

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Reset page, search, and filters on category (gender) change
  const handleCategoryChange = (newCat) => {
    setCategory(newCat);
    setSearchTerm("");
    setDebouncedSearch("");
    setFilters({});
    setPage(1);
  };

  // Reset page, search, and filters on modelType (mode) change
  const handleModelTypeChange = (newMode) => {
    setModelType(newMode);
    setSearchTerm("");
    setDebouncedSearch("");
    setFilters({});
    setPage(1);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters || {});
    setPage(1);
  };

  // Fetch products from API with search and filter parameters
  const fetchProducts = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      let url = `${endpoint.API_BASE_URL}${endpoint.GET_PRODUCTS}?gender=${category}&mode=${mappedModelType}&page=${page}&limit=${limit}`;

      // Dynamic search query param
      if (debouncedSearch && debouncedSearch.trim()) {
        url += `&search=${encodeURIComponent(debouncedSearch.trim())}`;
      }

      // Dynamic collection filter query param if selected
      const rawCollection = filters?.collection ?? filters?.collections ?? [];
      const selectedCollection = Array.isArray(rawCollection)
        ? rawCollection[0]
        : typeof rawCollection === "string"
          ? rawCollection
          : null;
      if (
        selectedCollection &&
        selectedCollection.trim() &&
        selectedCollection.trim().toLowerCase() !== "all"
      ) {
        url += `&collection=${encodeURIComponent(selectedCollection.trim())}`;
      }

      // Dynamic category filter query param if selected
      const rawCategory = filters?.category ?? filters?.categories ?? [];
      const selectedCategory = Array.isArray(rawCategory)
        ? rawCategory[0]
        : typeof rawCategory === "string"
          ? rawCategory
          : null;
      if (
        selectedCategory &&
        selectedCategory.trim() &&
        selectedCategory.trim().toLowerCase() !== "all"
      ) {
        url += `&category=${encodeURIComponent(selectedCategory.trim())}`;
      }

      // Dynamic price filter query param if selected
      const rawPrice = filters?.price ?? [];
      const selectedPrice = Array.isArray(rawPrice)
        ? rawPrice[0]
        : typeof rawPrice === "string"
          ? rawPrice
          : null;
      if (
        selectedPrice &&
        selectedPrice.trim() &&
        selectedPrice.trim().toLowerCase() !== "all"
      ) {
        url += `&price=${encodeURIComponent(selectedPrice.trim())}`;
      }

      const response = await apiHandler({
        url,
        method: "GET",
        authToken: { token },
        noCache: true,
      });

      const data = response?.data?.data;
      const returnedFilters = data?.filters || response?.data?.filters || null;
      if (Array.isArray(returnedFilters) && returnedFilters.length > 0) {
        setApiFilters((prev) => (prev.length > 0 && returnedFilters.length < prev.length ? prev : returnedFilters));
      }

      const collections = normalizeProductCollections(data);
      setRawCollections(collections);

      const totalCollections =
        Number(
          data?.total ??
            response?.data?.total ??
            data?.totalCount ??
            response?.data?.totalCount ??
            data?.pagination?.total ??
            collections.length
        ) || collections.length;

      const totalP =
        Number(
          data?.totalPages ??
            response?.data?.totalPages ??
            data?.pagination?.totalPages
        ) || Math.max(1, Math.ceil(totalCollections / limit));

      setTotal(totalCollections);
      setTotalPages(totalP);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(err?.message || "Failed to load products.");
      setRawCollections([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [token, category, mappedModelType, page, limit, debouncedSearch, filters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Data after filter is fetched directly from API
  const filteredCollections = useMemo(() => {
    return rawCollections.filter(
      (col) => Array.isArray(col.products) && col.products.length > 0
    );
  }, [rawCollections]);

  // Total product count across all filtered collections
  const totalProductsCount = useMemo(() => {
    return filteredCollections.reduce((acc, col) => acc + (col.products?.length || 0), 0);
  }, [filteredCollections]);

  // Status Toggle
  const handleToggleProduct = async (product) => {
    const itemId = resolvePlaygroundItemId(product);
    if (!itemId) {
      toast.error("Product identifier missing.");
      return;
    }

    const previousState = Boolean(product.bIsActive);
    const newState = !previousState;

    // Optimistic UI update across collections
    setRawCollections((prev) =>
      prev.map((col) => ({
        ...col,
        products: (col.products || []).map((item) =>
          resolvePlaygroundItemId(item) === itemId
            ? { ...item, bIsActive: newState, isActive: newState }
            : item
        ),
      }))
    );

    try {
      const response = await apiHandler({
        url: `${endpoint.TOGGLE_PRODUCT}/${itemId}/toggle`,
        method: "PATCH",
        authToken: { token },
        noCache: true,
      });

      if (
        (response?.status && response.status >= 400) ||
        response?.data?.status === "error" ||
        response?.data?.success === false
      ) {
        throw new Error(response?.data?.message || "Failed to update status");
      }

      toast.success(`Product ${newState ? "activated" : "deactivated"} successfully.`);
    } catch (err) {
      console.error("Toggle error:", err);
      toast.error(err?.message || "Failed to update product status.");
      // Rollback
      setRawCollections((prev) =>
        prev.map((col) => ({
          ...col,
          products: (col.products || []).map((item) =>
            resolvePlaygroundItemId(item) === itemId
              ? { ...item, bIsActive: previousState, isActive: previousState }
              : item
          ),
        }))
      );
    }
  };

  // Modal Handlers
  const handleUploadModalOpen = () => {
    setEditingProduct(null);
    setIsUploadModalOpen(true);
  };

  const handleEditModalOpen = (product) => {
    const enrichedProduct = product
      ? {
          ...product,
          gender: product.gender || product.genderName || product.sGender || category,
          mode: product.mode || product.modeName || product.sMode || product.modelType || mappedModelType,
        }
      : product;
    setEditingProduct(enrichedProduct);
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
    setEditingProduct(null);
  };

  const handleUploadSubmit = async (payload, productToEdit) => {
    try {
      if (productToEdit) {
        const productId = resolvePlaygroundItemId(productToEdit) || productToEdit.id || productToEdit.iProductId;
        const result = await updateProduct(productId, payload);
        if (result.success) {
          toast.success("Product updated successfully!");
          handleUploadModalClose();
          fetchProducts();
          return result;
        } else {
          toast.error(`Update failed: ${result.error}`);
          return result;
        }
      } else {
        const result = await uploadProduct(payload);
        if (result.success) {
          toast.success("Product uploaded successfully!");
          handleUploadModalClose();
          fetchProducts();
          return result;
        } else {
          toast.error(`Upload failed: ${result.error}`);
          return result;
        }
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      return { success: false, error: error.message };
    }
  };

  // Zoom modal actions
  const openZoomModal = (front, back = "") => {
    setZoomModal({ front, back });
  };

  const closeZoomModal = () => {
    setZoomModal({ front: "", back: "" });
  };

  return {
    userRole,
    category,
    onCategoryChange: handleCategoryChange,
    modelType,
    mappedModelType,
    onModelTypeChange: handleModelTypeChange,
    searchTerm,
    onSearchChange: (e) => setSearchTerm(e?.target ? e.target.value : e),
    filters,
    onFilterChange: handleFilterChange,
    apiFilters,
    collections: filteredCollections,
    totalProductsCount,
    loading,
    error,
    refreshProducts: fetchProducts,
    pagination: {
      page,
      limit,
      total,
      totalPages,
    },
    onPageChange: (newPage) => setPage(newPage),
    handleToggleProduct,
    // Modals
    isUploadModalOpen,
    editingProduct,
    isBulkUploadModalOpen,
    isBulkImageUploadModalOpen,
    isProductUploadLoading,
    handleUploadModalOpen,
    handleEditModalOpen,
    handleUploadModalClose,
    handleBulkUploadModalOpen: () => setIsBulkUploadModalOpen(true),
    handleBulkUploadModalClose: () => setIsBulkUploadModalOpen(false),
    handleBulkImageUploadModalOpen: () => setIsBulkImageUploadModalOpen(true),
    handleBulkImageUploadModalClose: () => {
      setIsBulkImageUploadModalOpen(false);
      fetchProducts();
    },
    handleUploadSubmit,
    fetchFilterCategories,
    fetchCollectionCategories,
    // Zoom
    zoomModal,
    openZoomModal,
    closeZoomModal,
  };
};
